import { addDays } from "date-fns";
import { Variant } from "@telia/teddy/dist/components/badge/badge";
import _isString from "lodash/isString";
import moment from "moment";
import { MISSING_IMEI_MAX_DAYS_BACK, RETAILER_SUBSCRIPTION_LABELS } from "../../../constants";
import PAGES from "../../../constants/pages";
import { RetailStepIndicatorType } from "../../../models";
import { ChangeScope, CustomerExistingAccount, CustomerExistingSubscription } from "../../../models/state/retailer";
import { isNonMhsSubscription } from "../../../shared/subscriptionCodeUtils";
import _capitalize from "lodash/capitalize";

export const pending = (actionType: string) => `${actionType}_PENDING`;
export const fulfilled = (actionType: string) => `${actionType}_FULFILLED`;
export const rejected = (actionType: string) => `${actionType}_REJECTED`;

export const goBackToOrdersOverview = () => {
    const ordersOverviewUrl = PAGES.RETAILER.ORDERS_OVERVIEW.URL;
    try {
        const referrer = new URL(document.referrer);
        if (referrer.origin === window.location.origin && referrer.pathname === ordersOverviewUrl) {
            window.location.replace(referrer.href);
            return;
        }
    } catch {
        // no or invalid referrer
    }
    window.location.assign(ordersOverviewUrl);
};

export const getRetailerStepIndicatorData = (): RetailStepIndicatorType => {
    return {
        hide: false,
        steps: 3,
        labels: RETAILER_SUBSCRIPTION_LABELS,
    };
};

export const isPhoneNumberUsable = (phoneNumber: string) =>
    _isString(phoneNumber) && (/^[94]\d{7}$/.test(phoneNumber) || /^[5]\d{11}$/.test(phoneNumber));

/**
 * Returns true for subscriptions that count toward the max-8-per-BAN limit.
 * Telia Trygg, Kontant and MBB are excluded from the count.
 */
export const isCountableSubscription = (sub: CustomerExistingSubscription) => !isNonMhsSubscription(sub.offeringCode);

export const isSubscriptionActive = (sub: CustomerExistingSubscription) => {
    // ACTIVE: "A",
    // RESERVED: "R",
    // SUSPENDED: "S",
    // CANCELLED: "C",
    return sub.status === "A";
};

/**
 * Strips everything but digits and drops a leading "+47"/"0047" country code
 * so numbers looked up with or without it can still be compared. Unlike a
 * generic last-8-digits truncation, this keeps the full number for longer
 * formats (e.g. 12-digit MBB numbers), avoiding false matches between
 * different subscriptions/accounts that happen to share the same last 8 digits.
 */
const normalizePhoneNumberForComparison = (phoneNumber?: string): string => {
    const digitsOnly = (phoneNumber || "").replace(/\D/g, "");
    if (digitsOnly.startsWith("0047")) {
        return digitsOnly.slice(4);
    }
    if (digitsOnly.startsWith("47") && digitsOnly.length > 8) {
        return digitsOnly.slice(2);
    }
    return digitsOnly;
};

export const accountHasPhoneNumber = (account: CustomerExistingAccount, phoneNumber?: string): boolean => {
    const normalizedLookup = normalizePhoneNumberForComparison(phoneNumber);
    if (!normalizedLookup) {
        return false;
    }
    return account.subscriptions.some((sub) => normalizePhoneNumberForComparison(sub.phoneNumber) === normalizedLookup);
};

/**
 * Checks if a commitment period is currently active.
 * A commitment is considered active if:
 * 1. There is a commitment end date
 * 2. The end date is today or in the future
 */
export const isCommitmentActive = (commitmentEndDate?: string | number): boolean => {
    if (!commitmentEndDate) {
        return false;
    }
    const today = moment().startOf("day");
    const endDate = moment(commitmentEndDate).startOf("day");
    return endDate.isAfter(today) || endDate.isSame(today);
};

const changeScopeBadgeText: Partial<Record<ChangeScope, string>> = {
    full: "Endret",
    sim: "SIM-kort endret",
    productUser: "Bruker endret",
    vas: "Tilleggstjenester endret",
    subscriptionChange: "Abonnement endret",
};

export const getStatusBadge = (
    status?: string,
    isActive?: boolean,
    isChanged?: boolean,
    changeScope?: ChangeScope,
): { text: string; variant: Variant } | undefined => {
    if (isChanged) {
        return { text: changeScopeBadgeText[changeScope ?? "full"] ?? "Endret", variant: "success" };
    }

    switch (true) {
        case status === "C": {
            return {
                variant: "warning",
                text: "Kansellert",
            };
        }
        case status === "R":
        case status === "S":
        case isActive === false:
            return {
                variant: "neutral",
                text: "Inaktiv",
            };
    }
};

const DATE_FORMAT_DDMMYYYY_WITH_SEPARATION = (separator: string) => ["DD", "MM", "YYYY"].join(separator);
const hasLeaseInfo = (subscription: Pick<CustomerExistingSubscription, "leaseInfo">): boolean =>
    (subscription.leaseInfo?.length ?? 0) > 0;

export const getExistingSubscriptionTitle = (subscription: CustomerExistingSubscription) => {
    const commitmentTitle = subscription.switchAgreement
        ? "Aktiv Svitsj-avtale"
        : isCommitmentActive(subscription.commitmentEndDate)
          ? "Avtaletid til " +
            moment(subscription.commitmentEndDate).format(DATE_FORMAT_DDMMYYYY_WITH_SEPARATION(".")) +
            (hasLeaseInfo(subscription) ? " med delbetaling" : "")
          : "Ingen avtaletid";
    const productUser = subscription?.firstName
        ? subscription.firstName.charAt(0).toUpperCase() + subscription.firstName.slice(1).toLowerCase()
        : "";
    const title = productUser ? `${commitmentTitle} - ${productUser}` : commitmentTitle;
    return title;
};

const hasBreakageFee = (
    subscription: Pick<CustomerExistingSubscription, "switchAgreement" | "commitmentEndDate" | "breakageFeeAmount">,
    isCustomerDataFetched: boolean,
): boolean =>
    isCustomerDataFetched &&
    !subscription.switchAgreement &&
    isCommitmentActive(subscription.commitmentEndDate) &&
    !!subscription.breakageFeeAmount;

export const getSubscriptionTitleWithBreakageFee = (
    subscription: CustomerExistingSubscription,
    isCustomerDataFetched: boolean,
): string => {
    const title = isCustomerDataFetched
        ? getExistingSubscriptionTitle(subscription)
        : isCommitmentActive(subscription.commitmentEndDate)
          ? "Har avtaletid" + (hasLeaseInfo(subscription) ? " med delbetaling" : "")
          : "Ingen avtaletid";

    return hasBreakageFee(subscription, isCustomerDataFetched)
        ? `${title}<br />Mulig bruddgebyr: ${subscription.breakageFeeAmount},-`
        : title;
};

export const hasDisplayableMessage = (err: unknown): err is Error & { displayableMessage: string } => {
    return (
        err instanceof Error &&
        "displayableMessage" in err &&
        typeof (err as Record<string, unknown>).displayableMessage === "string"
    );
};

export const getYearlyCommitmentDiscountRate = (offeringCode?: string) => {
    if (offeringCode?.includes("COM12PURP30")) {
        return 0.3;
    }

    if (offeringCode?.includes("COM12PURP25")) {
        return 0.25;
    }

    if (offeringCode?.includes("COM12PURP20")) {
        return 0.2;
    }

    if (offeringCode?.includes("COM12PURP15")) {
        return 0.15;
    }

    if (offeringCode?.includes("COM12PURP10")) {
        return 0.1;
    }

    return 0;
};

/**
 * Badge label for the yearly commitment discount, e.g. "Årsavtale -20%". Returns
 * undefined when the offering code doesn't carry a yearly commitment discount.
 */
export const getYearlyCommitmentDiscountLabel = (offeringCode?: string): string | undefined => {
    const rate = getYearlyCommitmentDiscountRate(offeringCode);
    return rate > 0 ? `Årsavtale -${Math.round(rate * 100)}%` : undefined;
};

export const calculateEffectivePrice = (
    sub: Pick<CustomerExistingSubscription, "price" | "priceLines" | "offeringCode">,
    familyDiscount?: boolean,
): { effectivePrice: number; priceStriketrough: number | undefined } => {
    const originalPrice = Number(sub.price) || 0;
    const priceAfterFamilyDiscount =
        familyDiscount && sub.priceLines !== null && sub.priceLines !== undefined
            ? Number(sub.priceLines)
            : originalPrice;

    const commitmentDiscountAmount = Math.floor(
        priceAfterFamilyDiscount * getYearlyCommitmentDiscountRate(sub.offeringCode),
    );

    const effectivePrice = priceAfterFamilyDiscount - commitmentDiscountAmount;
    const priceStriketrough = effectivePrice < originalPrice ? originalPrice : undefined;

    return { effectivePrice, priceStriketrough };
};

type CommissionBadgeVariant = "success" | "warning" | "error";

export interface CommissionDeadlineStatus {
    label: string;
    variant: CommissionBadgeVariant;
}

export const getCommissionDeadlineStatus = (creationDate: string): CommissionDeadlineStatus | null => {
    const parsed = new Date(creationDate);
    if (isNaN(parsed.getTime())) return null;
    const deadline = addDays(parsed, MISSING_IMEI_MAX_DAYS_BACK);
    const diffMs = deadline.getTime() - Date.now();
    if (diffMs <= 0) {
        return { label: "Frist utløpt", variant: "error" };
    }
    const oneHourMs = 60 * 60 * 1000;
    const oneDayMs = 24 * oneHourMs;
    const sevenDaysMs = 7 * oneDayMs;
    let label: string;
    if (diffMs >= oneDayMs) {
        const days = Math.floor(diffMs / oneDayMs);
        label = days === 1 ? "1 dag" : `${days} dager`;
    } else if (diffMs >= oneHourMs) {
        const hours = Math.floor(diffMs / oneHourMs);
        label = hours === 1 ? "1 time" : `${hours} timer`;
    } else {
        label = "< 1 time";
    }
    const variant: CommissionBadgeVariant = diffMs < sevenDaysMs ? "warning" : "success";
    return { label, variant };
};

export const capitalizeEachWord = (str: string): string => {
    return str
        .split(" ")
        .map((word) => _capitalize(word))
        .join(" ");
};

export const isScopedChange = (scope: ChangeScope | undefined): boolean => {
    return scope === "sim" || scope === "productUser" || scope === "vas";
};

export const isSubscriptionScope = (scope: ChangeScope | undefined): boolean => {
    return scope === "subscriptionChange" || scope === "full" || !scope;
};
